import React, { useState } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";

interface GxSmallDropdownProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  className?: string;
  searchable?: boolean;
  fullWidth?: boolean;
  minWidth?: string;
  maxWidth?: string;
  disabled?: boolean;
}

const GxSmallDropdown: React.FC<GxSmallDropdownProps> = ({
  id = "gx-small-dropdown",
  value,
  onChange,
  options,
  className = "",
  searchable = false,
  fullWidth = false,
  minWidth,
  maxWidth,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const filteredOptions =
    searchable && searchText
      ? options.filter((option) =>
          option.toLowerCase().includes(searchText.toLowerCase()),
        )
      : options;

  const data = filteredOptions.map((option) => ({
    text: option,
    value: option,
  }));

  const selectedItem = data.find((item) => item.value === value) ?? null;

  const noDataMessage =
    searchText && filteredOptions.length === 0
      ? `No results found for "${searchText}"`
      : "No data available";

  const wrapperStyle: React.CSSProperties = {
    width: fullWidth ? "100%" : "auto",
    minWidth,
    maxWidth,
  };

  return (
    <div className="gx-small-dropdown-wrapper" style={wrapperStyle}>
      <DropDownList
        id={id}
        data={data}
        textField="text"
        dataItemKey="value"
        value={selectedItem}
        disabled={disabled}
        ariaLabelledBy={`${id}-label`}
        onChange={(e) => {
          if (e.value) {
            onChange(e.value.value);
          }
          setSearchText("");
        }}
        className={`gx-small-dropdown ${
          fullWidth ? "gx-small-dropdown-full" : ""
        } ${className}`}
        popupSettings={{
          className: `gx-small-dropdown-popup ${
            searchable ? "gx-dropdown-with-search" : ""
          }`,
        }}
        onOpen={() => {
          setIsOpen(true);
          setSearchText("");
        }}
        onClose={() => {
          setIsOpen(false);
          setSearchText("");
        }}
        listNoDataRender={() => <div>{noDataMessage}</div>}
        header={
          searchable ? (
            <div
              className="gx-dropdown-search-container"
              onMouseDown={(e) => e.preventDefault()}
            >
              <input
                type="text"
                className="gx-dropdown-search-input"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                autoFocus
              />

              <div className="gx-dropdown-search-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          ) : undefined
        }
      />

      <div
        className={`gx-dropdown-arrow ${isOpen ? "rotate" : ""}`}
        aria-hidden="true"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M7 10L12 15L17 10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
};

export default GxSmallDropdown;
